import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { AppointmentFormFields } from './AppointmentFormFields';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FIELDS = ['title', 'appointment_type', 'location_type', 'date', 'start_time', 'end_time', 'location', 'notes', 'case_manager_id'];

export const SeriesScopeChoice = ({ value, onChange, verb, testid }) => (
  <div className="space-y-2 rounded-lg border p-3">
    <Label>This is a repeating appointment. {verb}:</Label>
    <RadioGroup value={value} onValueChange={onChange} data-testid={testid}>
      <div className="flex items-center gap-2"><RadioGroupItem value="one" id={`${testid}-one`} data-testid={`${testid}-one`} /><Label htmlFor={`${testid}-one`} className="font-normal">This date only</Label></div>
      <div className="flex items-center gap-2"><RadioGroupItem value="following" id={`${testid}-following`} data-testid={`${testid}-following`} /><Label htmlFor={`${testid}-following`} className="font-normal">This and all later dates in the series</Label></div>
    </RadioGroup>
  </div>
);

export const AppointmentEditDialog = ({ appointment, onClose, onSaved }) => {
  const [form, setForm] = useState(null);
  const [scope, setScope] = useState('one');
  const [types, setTypes] = useState([]);
  const [staff, setStaff] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!appointment) return;
    setForm(Object.fromEntries(FIELDS.map((k) => [k, appointment[k] ?? (k === 'location_type' ? 'in_person' : '')])));
    setScope('one');
    axios.get(`${API}/appointment-types`).then((r) => setTypes(r.data.appointment_types || [])).catch(() => {});
    axios.get(`${API}/users/staff`).then((r) => setStaff(r.data.users || [])).catch(() => {});
  }, [appointment]);

  const save = async () => {
    if (!form.title?.trim() || !form.date || !form.start_time) { toast.error('Please fill in title, date and start time'); return; }
    if (form.end_time && form.end_time <= form.start_time) { toast.error('End time must be after the start time'); return; }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/appointments/${appointment.id}/details`, { ...form, case_manager_id: form.case_manager_id || null, scope });
      toast.success(res.data.appointments_updated > 1 ? `Updated ${res.data.appointments_updated} appointments` : 'Appointment updated');
      onSaved(res.data.appointment);
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Failed to update appointment');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={Boolean(appointment)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="appointment-edit-dialog">
        <DialogHeader>
          <DialogTitle>Update Appointment</DialogTitle>
          <DialogDescription>{appointment?.participant_name ? `Appointment for ${appointment.participant_name}` : 'Change the appointment details'}</DialogDescription>
        </DialogHeader>
        {form && (
          <div className="space-y-4 py-4">
            <AppointmentFormFields value={form} onChange={(p) => setForm((f) => ({ ...f, ...p }))} appointmentTypes={types} staffList={staff} idPrefix="edit-appointment" />
            {appointment?.series_id && <SeriesScopeChoice value={scope} onChange={setScope} verb="Apply changes to" testid="edit-appointment-scope" />}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="edit-appointment-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={saving || !form} data-testid="edit-appointment-save-btn">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Save Changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
